/**
 * VideoConfigFlyout — Config flyout content for the video node control bar.
 *
 * Six control groups: Model, Aspect Ratio, Resolution, Duration, Model Audio,
 * and Allow BGM.
 * Resolution and Duration rendering are model-dependent via getVideoConstraints().
 * All changes persist immediately via onChange callbacks.
 *
 * The Model section fetches available models from /api/video-models and splits
 * them into "Dreamwrite" (native) and "My Video Model" (user-registered BYOM) sections.
 */

import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Key, Star } from "lucide-react";
import {
  getVideoConstraints,
  getByomConstraints,
  getByomAspectRatios,
  getNativeVideoAspectRatios,
  isByomModel,
} from "./videoConstraints";
import { hasCapabilities } from "./generationPreflight";
import { APP_NAME } from "../../../../constants/brand";
import { getVideoModelLabel } from "../../../../constants/modelAliases";
import { getVideoModelDisplayName } from "./videoModelDisplayName";
import { getVideoResolutionDisplayName } from "./videoResolutionDisplayName";
import { useVideoModels } from "../../../../hooks/useVideoModels";
import type { ByomModelInfo } from "../../../../api/client";
import { createNadDebug } from "../../../../utils/debugLog";
import { useNuxTooltipEvents } from "../../../../onboarding/nuxEvents";

const log = createNadDebug("video-config-flyout");
const durationSelectorLog = createNadDebug("dw:video-duration-selector");
const audioSplitLog = createNadDebug("dw:video-audio-mode-split:flyout");
const modelValidationLog = createNadDebug("dw:video-model-validation:flyout");

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VideoConfigFlyoutProps {
  nodeId?: string;
  model: string;
  onModelChange: (model: string) => void;
  aspectRatio: string;
  onAspectRatioChange: (ratio: string) => void;
  resolution: string;
  onResolutionChange: (resolution: string) => void;
  duration: number;
  referenceImageCount?: number;
  onDurationChange: (duration: number) => void;
  providerGenerateAudio: boolean;
  onProviderGenerateAudioChange: (providerGenerateAudio: boolean) => void;
  allowBgm: boolean;
  onAllowBgmChange: (allowBgm: boolean) => void;
  modelUnavailableReason?: string;
  unavailableModelId?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const INTERNAL_EVALUATION_VIDEO_MODEL_IDS = new Set([
  "gemini-omni-flash-preview",
  "gemini-omni-1.1-flash-preview",
  // Edit, Extend and I2V are the same Vertex model as the base 1.1 entry above and
  // are under the same evaluation, so they carry the same marker. Omitting
  // them would render the siblings inconsistently for no reason a user
  // could infer. Note this set is NOT a public/internal gate — the base 1.1
  // entry is public and still in it; it drives a tone and a hover title.
  "gemini-omni-1.1-flash-edit",
  "gemini-omni-1.1-flash-extend",
  "gemini-omni-1.1-flash-i2v",
  "seedance-2.0-image-to-video",
  "seedance-2.0-reference-to-video",
  "seedance-2.0-image-to-video-atlas-cloud",
  "seedance-2.0-reference-to-video-atlas-cloud",
  "seedance-2.5-image-to-video-atlas-cloud",
  "seedance-2.5-first-last-frame-atlas-cloud",
  "seedance-2.5-reference-to-video-atlas-cloud",
  "seedance-2.5-video-editing-atlas-cloud",
  "seedance-2.5-video-extension-atlas-cloud",
  "minimax-h3-reference-to-video",
  "minimax-h3-text-to-video-atlas-cloud",
  "minimax-h3-image-to-video-atlas-cloud",
  "minimax-h3-reference-to-video-atlas-cloud",
  "minimax-h3-max-turbo-text-to-video-atlas-cloud",
  "minimax-h3-max-turbo-image-to-video-atlas-cloud",
  "minimax-h3-fast-text-to-video-atlas-cloud",
  "minimax-h3-fast-image-to-video-atlas-cloud",
  "minimax-h3-fast-reference-to-video-atlas-cloud",
  "mango-video-text-to-video",
  "mango-video-image-to-video",
]);
const LONG_DURATION_OPTION_THRESHOLD = 8;

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const LABEL_CLASS =
  "text-xxs uppercase tracking-wider text-text-muted block mb-1.5";
const SECTION_HEADER_CLASS =
  "text-xxs uppercase tracking-wider text-text-muted/60 mb-1";

type SegmentedButtonTone = "default" | "internal";
type SegmentedButtonLayout = "fill" | "chip" | "wrap";

function isInternalEvaluationVideoModel(model: string): boolean {
  return INTERNAL_EVALUATION_VIDEO_MODEL_IDS.has(model);
}

function segmentedBtnClass(
  isSelected: boolean,
  isDisabled = false,
  tone: SegmentedButtonTone = "default",
  layout: SegmentedButtonLayout = "fill",
): string {
  const isInternal = tone === "internal";
  const stateClasses = isSelected
    ? isInternal
      ? "bg-primary/20 text-primary border border-agent-sound/80"
      : "bg-primary/20 text-primary border border-primary/40"
    : isDisabled
      ? "bg-surface-1 text-text-muted border border-border-subtle"
      : isInternal
        ? "bg-surface-2 text-text-muted border border-agent-sound/60 hover:bg-surface-3 hover:text-foreground hover:border-agent-sound/80"
        : "bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground";
  const layoutClasses = layout === "chip"
    ? "flex-none max-w-full px-2.5"
    : layout === "wrap"
      ? "basis-16 min-w-16 grow"
      : "flex-1 min-w-0";
  return `inline-flex ${layoutClasses} items-center justify-center py-1.5 rounded-md text-xs font-medium transition-colors ${stateClasses} ${
    isDisabled ? "opacity-60 cursor-not-allowed" : ""
  }`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VideoConfigFlyout = memo(function VideoConfigFlyout({
  nodeId,
  model,
  onModelChange,
  aspectRatio,
  onAspectRatioChange,
  resolution,
  onResolutionChange,
  duration,
  onDurationChange,
  referenceImageCount = 0,
  providerGenerateAudio,
  onProviderGenerateAudioChange,
  allowBgm,
  onAllowBgmChange,
  modelUnavailableReason,
  unavailableModelId,
}: VideoConfigFlyoutProps) {
  const { t } = useTranslation('canvas');
  const { emitNuxEvent } = useNuxTooltipEvents();
  const emitModelChanged = () => {
    emitNuxEvent({
      type: 'controlActivated',
      controlId: 'videoModelChanged',
      anchorId: 'video-generator-model-selection',
      nodeId,
      nodeType: 'videoGenerator',
    });
  };
  // Look up BYOM model for capability-driven constraints
  const {
    native: apiNativeModels,
    byom: byomModels,
    isLoading: modelsLoading,
    isReady: modelsReady,
  } = useVideoModels();

  const selectedByom = useMemo<ByomModelInfo | undefined>(
    () =>
      isByomModel(model) ? byomModels.find((b) => b.id === model) : undefined,
    [model, byomModels],
  );

  // All options derive from the selected model input contract.
  const constraintModel = model.replace(/^gateway\//, "");
  const constraints = useMemo(
    () => {
      if (isByomModel(model)) {
        return modelsReady && selectedByom?.capabilities
          ? getByomConstraints(selectedByom.capabilities, { duration, resolution })
          : {
              resolutionOptions: null,
              resolutionDefault: resolution,
              resolutionLabel: resolution,
              durationOptions: null,
              durationDefault: duration,
              durationLabel: `${duration}s`,
            }
      }
      if (!hasCapabilities(constraintModel)) return {
        resolutionOptions: null, resolutionDefault: resolution, resolutionLabel: resolution,
        durationOptions: null, durationDefault: duration, durationLabel: `${duration}s`,
      }
      return getVideoConstraints(constraintModel, resolution, referenceImageCount)
    },
    [constraintModel, duration, model, modelsReady, resolution, selectedByom, referenceImageCount],
  );
  const durationOptions = constraints.durationOptions;
  const usesCompactDurationSelector = Boolean(
    durationOptions && durationOptions.length > LONG_DURATION_OPTION_THRESHOLD,
  );
  const durationControlVariant = !durationOptions
    ? "static"
    : usesCompactDurationSelector
      ? "select"
      : "buttons";
  const durationModeReason = !durationOptions
    ? "model_fixed_duration"
    : usesCompactDurationSelector
      ? "many_duration_options"
      : "few_duration_options";
  const durationSelectorTraceId = `video-duration-selector:${constraintModel}:${resolution}`;

  useEffect(() => {
    durationSelectorLog({
      event: "duration-selector:render",
      trace_id: durationSelectorTraceId,
      model,
      constraint_model: constraintModel,
      resolution,
      selected_duration: duration,
      selected_duration_seconds: duration,
      mode: durationControlVariant,
      control_variant: durationControlVariant,
      type: durationControlVariant,
      mode_reason: durationModeReason,
      reason: durationModeReason,
      option_count: durationOptions?.length ?? 0,
      target_count: durationOptions?.length ?? 0,
      selectedCount: durationOptions?.includes(duration) ? 1 : 0,
      resolvedValue: duration,
    });
  }, [
    constraintModel,
    duration,
    durationControlVariant,
    durationModeReason,
    durationOptions,
    durationSelectorTraceId,
    model,
    resolution,
  ]);

  // Aspect ratios: BYOM models may have custom aspect ratios
  const aspectRatioOptions = useMemo(
    () =>
      isByomModel(model)
        ? modelsReady && selectedByom?.capabilities
          ? getByomAspectRatios(selectedByom.capabilities, aspectRatio)
          : [aspectRatio]
        : getNativeVideoAspectRatios(constraintModel),
    [aspectRatio, constraintModel, model, modelsReady, selectedByom],
  );

  // Native models come exclusively from the API — no hardcoded fallback list
  // that can get out of sync with the backend and cause a flash of stale models.
  const nativeModels = useMemo(() => apiNativeModels, [apiNativeModels]);

  const nativeModelOptions = useMemo(
    () =>
      nativeModels.map((value) => ({
        value,
        label: getVideoModelLabel(value),
      })),
    [nativeModels],
  );

  const myVideoModelOptions = useMemo(
    () =>
      byomModels.map((b) => ({
        value: b.id,
        label: getVideoModelDisplayName(b.id, byomModels),
        isDefault: b.is_default,
        enabled: b.enabled !== false,
      })),
    [byomModels],
  );

  const hasMyModels = myVideoModelOptions.length > 0;
  const isModelUnavailable = Boolean(modelUnavailableReason);

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="video-config-flyout-content"
      data-model-validity={isModelUnavailable ? "invalid" : "valid"}
    >
      {/* Model */}
      <div
        data-nux-anchor={
          nativeModelOptions.length > 0 || myVideoModelOptions.length > 0 || modelsLoading
            ? "video-generator-model-selection"
            : undefined
        }
        data-nux-node-id={nodeId}
        data-nux-node-type="videoGenerator"
      >
        <label className={LABEL_CLASS}>{t('videoConfigFlyout.model')}</label>

        {isModelUnavailable && (
          <div
            className="mb-2 rounded-lg border border-warning/30 bg-warning/10 px-2 py-1.5 text-xs font-medium text-warning"
            data-testid="video-model-unavailable-flyout-alert"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <div>
                <div>{modelUnavailableReason}</div>
                {unavailableModelId && (
                  <div
                    className="mt-0.5 break-all font-mono text-xxs text-warning/90"
                    data-testid="video-unavailable-model-id-flyout"
                  >
                    {unavailableModelId}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Native (Dreamwrite) models */}
        {hasMyModels && <div className={SECTION_HEADER_CLASS}>{APP_NAME}</div>}
        <div
          className="flex flex-wrap items-start gap-1"
          data-testid="native-video-model-chips"
        >
          {modelsLoading && nativeModelOptions.length === 0 ? (
            <div
              className="h-[30px] w-24 rounded-md bg-surface-2 animate-pulse"
              data-testid="model-loading-skeleton"
            />
          ) : (
            nativeModelOptions.map((opt) => {
              const isInternalModel = isInternalEvaluationVideoModel(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  title={
                    isInternalModel ? t('videoConfigFlyout.internalEvaluationModel') : undefined
                  }
                  onClick={() => {
                    modelValidationLog({
                      event: "model:select-valid-native",
                      previous_model: model,
                      selected_model: opt.value,
                      was_invalid: isModelUnavailable,
                    });
                    emitModelChanged();
                    onModelChange(opt.value);
                  }}
                  className={segmentedBtnClass(
                    model === opt.value,
                    false,
                    isInternalModel ? "internal" : "default",
                    "chip",
                  )}
                  data-internal-model={isInternalModel ? "true" : undefined}
                  data-testid={`model-${opt.value}`}
                >
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })
          )}
        </div>

        {/* My Video Model (user-registered BYOM models) */}
        {myVideoModelOptions.length > 0 && (
          <>
            <div className={`${SECTION_HEADER_CLASS} mt-2`}>{t('videoConfigFlyout.myVideoModel')}</div>
            <div
              className="flex flex-wrap items-start gap-1"
              data-testid="byom-model-chips"
            >
              {myVideoModelOptions.map((opt) => {
                const selectionDisabled = !modelsReady || !opt.enabled
                const statuses = [
                  opt.isDefault ? t('videoConfigFlyout.default') : null,
                  !opt.enabled ? t('videoConfigFlyout.disabled') : null,
                ].filter((status): status is string => Boolean(status))
                const accessibleLabel = statuses.length > 0
                  ? `${opt.label} (${statuses.join(', ')})`
                  : opt.label

                return (
                  <button
                    key={`byom-${opt.value}`}
                    type="button"
                    disabled={selectionDisabled}
                    title={accessibleLabel}
                    aria-label={accessibleLabel}
                    onClick={() => {
                      if (selectionDisabled) return;
                      log({
                        event: "model:select-byom",
                        model_id: opt.value,
                        key_name: opt.label,
                      });
                      modelValidationLog({
                        event: "model:select-valid-byom",
                        previous_model: model,
                        selected_model: opt.value,
                        key_name: opt.label,
                        was_invalid: isModelUnavailable,
                      });
                      emitModelChanged();
                      onModelChange(opt.value);
                    }}
                    className={`${segmentedBtnClass(
                      model === opt.value,
                      selectionDisabled,
                      "default",
                      "chip",
                    )} overflow-hidden px-2`}
                    data-testid={`model-byom-${opt.value}`}
                  >
                    <span className="flex w-full min-w-0 max-w-full items-center justify-center gap-1 overflow-hidden">
                      <Key
                        aria-hidden="true"
                        className="w-2.5 h-2.5 shrink-0 opacity-60"
                        data-testid={`model-byom-key-${opt.value}`}
                      />
                      <span
                        className="min-w-0 flex-1 truncate text-center"
                        title={opt.label}
                        data-testid={`model-byom-label-${opt.value}`}
                      >
                        {opt.label}
                      </span>
                      {opt.isDefault && (
                        <Star
                          aria-hidden="true"
                          className="w-2.5 h-2.5 shrink-0 text-primary fill-primary opacity-80"
                          data-testid={`model-byom-default-star-${opt.value}`}
                        />
                      )}
                      {!opt.enabled && (
                        <span
                          className="shrink-0 rounded border border-warning/30 bg-warning/10 px-1 py-0.5 text-xxs text-warning"
                          data-testid={`model-byom-disabled-badge-${opt.value}`}
                        >
                          {t('videoConfigFlyout.disabled')}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Aspect Ratio (dynamic for BYOM) */}
      <div>
        <label className={LABEL_CLASS}>{t('videoConfigFlyout.aspectRatio')}</label>
        <div className="flex flex-wrap gap-1">
          {aspectRatioOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => onAspectRatioChange(opt)}
              className={segmentedBtnClass(aspectRatio === opt, false, "default", "wrap")}
              data-testid={`aspect-ratio-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Resolution (model-dependent) */}
      <div>
        <label className={LABEL_CLASS}>{t('videoConfigFlyout.resolution')}</label>
        {constraints.resolutionOptions ? (
          <div className="flex flex-wrap gap-1">
            {constraints.resolutionOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => onResolutionChange(opt)}
                className={segmentedBtnClass(resolution === opt, false, "default", "wrap")}
                data-testid={`resolution-${opt}`}
              >
                {getVideoResolutionDisplayName(constraintModel, opt)}
              </button>
            ))}
          </div>
        ) : (
          <span
            className="text-xs font-medium text-text-secondary"
            data-testid="resolution-static"
          >
            {constraints.resolutionLabel ?? t('videoConfigFlyout.auto')}
          </span>
        )}
      </div>

      {/* Duration (model-dependent) */}
      <div>
        <label className={LABEL_CLASS}>{t('videoConfigFlyout.duration')}</label>
        {durationOptions ? (
          usesCompactDurationSelector ? (
            <select
              aria-label={t('videoConfigFlyout.duration')}
              className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-2.5 text-xs font-medium text-foreground outline-none transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:border-primary focus:ring-2 focus:ring-primary/20"
              data-testid="duration-select"
              value={durationOptions.includes(duration) ? String(duration) : ""}
              onChange={(event) => {
                const nextDuration = Number(event.currentTarget.value);
                durationSelectorLog({
                  event: "duration-selector:change",
                  trace_id: durationSelectorTraceId,
                  model,
                  constraint_model: constraintModel,
                  resolution,
                  previous_duration: duration,
                  previous_duration_seconds: duration,
                  selected_duration: nextDuration,
                  selected_duration_seconds: nextDuration,
                  mode: "select",
                  control_variant: "select",
                  type: "select",
                  mode_reason: "many_duration_options",
                  reason: "user_selected_duration",
                  option_count: durationOptions.length,
                  target_count: durationOptions.length,
                  selectedCount: 1,
                  requested: nextDuration,
                  previousValue: duration,
                  resolvedValue: nextDuration,
                });
                onDurationChange(nextDuration);
              }}
            >
              {!durationOptions.includes(duration) && (
                <option value="" disabled>
                  {duration}s
                </option>
              )}
              {durationOptions.map((d) => (
                <option key={d} value={d}>
                  {d}s
                </option>
              ))}
            </select>
          ) : (
            <div className="flex flex-wrap gap-1">
              {durationOptions.map((d) => (
                <button
                  key={d}
                  onClick={() => {
                    durationSelectorLog({
                      event: "duration-selector:change",
                      trace_id: durationSelectorTraceId,
                      model,
                      constraint_model: constraintModel,
                      resolution,
                      previous_duration: duration,
                      previous_duration_seconds: duration,
                      selected_duration: d,
                      selected_duration_seconds: d,
                      mode: "buttons",
                      control_variant: "buttons",
                      type: "buttons",
                      mode_reason: "few_duration_options",
                      reason: "user_selected_duration",
                      option_count: durationOptions.length,
                      target_count: durationOptions.length,
                      selectedCount: 1,
                      requested: d,
                      previousValue: duration,
                      resolvedValue: d,
                    });
                    onDurationChange(d);
                  }}
                  className={segmentedBtnClass(duration === d)}
                  data-testid={`duration-${d}s`}
                >
                  {d}s
                </button>
              ))}
            </div>
          )
        ) : (
          <span
            className="text-xs font-medium text-text-secondary"
            data-testid="duration-static"
          >
            {constraints.durationProviderSelected
              ? t('videoConfigFlyout.auto')
              : (constraints.durationLabel ?? `${constraints.durationDefault}s`)}
          </span>
        )}
      </div>

      {/* Model Audio */}
      <div>
        <label className={LABEL_CLASS}>{t('videoConfigFlyout.modelAudio')}</label>
        <div className="flex gap-1">
          <button
            onClick={() => {
              audioSplitLog({
                event: "audio:model:set",
                provider_generate_audio: true,
              });
              onProviderGenerateAudioChange(true);
            }}
            className={segmentedBtnClass(providerGenerateAudio === true)}
            data-testid="provider-generate-audio-on"
          >
            {t('videoConfigFlyout.on')}
          </button>
          <button
            onClick={() => {
              audioSplitLog({
                event: "audio:model:set",
                provider_generate_audio: false,
              });
              onProviderGenerateAudioChange(false);
            }}
            className={segmentedBtnClass(providerGenerateAudio === false)}
            data-testid="provider-generate-audio-off"
          >
            {t('videoConfigFlyout.off')}
          </button>
        </div>
      </div>

      {/* Allow BGM */}
      <div>
        <label className={LABEL_CLASS}>{t('videoConfigFlyout.allowBgm')}</label>
        <div className="flex gap-1">
          <button
            onClick={() => onAllowBgmChange(true)}
            className={segmentedBtnClass(allowBgm === true)}
            data-testid="allow-bgm-on"
          >
            {t('videoConfigFlyout.on')}
          </button>
          <button
            onClick={() => onAllowBgmChange(false)}
            className={segmentedBtnClass(allowBgm === false)}
            data-testid="allow-bgm-off"
          >
            {t('videoConfigFlyout.off')}
          </button>
        </div>
      </div>
    </div>
  );
});

VideoConfigFlyout.displayName = "VideoConfigFlyout";
